import React, { MouseEvent, ReactElement, useEffect, useMemo } from 'react';
import isEqual from 'lodash.isequal';
import { VariantType } from 'b2b-components/theme/components/ProductList';
import { HOT_JAR_TAG } from 'b2b-apps/shop/analytics/types/types';
import { tagHotJarRecording } from 'b2b-apps/shop/analytics/utils/analytics';
import { useDeviceType } from 'b2b-apps/shop/common/hooks';
import { ProductTileVariantsOptionType } from 'b2b-apps/shop/product/redux/products/tile/types';
import { ProductCategoryType } from 'b2b-apps/shop/product/redux/products/types';
import { ROUTES } from 'b2b-apps/shop/routes/components/Router';
import { useOpenLink, useRouterPush, useUrlCreator } from 'b2b-apps/shop/routes/hooks';
import { getCategoryUrlParams, getCdnImageUrl, getProductFallbackImgUrl } from 'b2b-apps/shop/routes/utils/url';
import { EnhancedComponentProps, EnhancedComponentType, InjectedProps, WrappedComponentType } from './';

const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    const { onClick, onAddToCart, ...originalProps } = props;
    const { product, sku: productSku } = originalProps;
    const push = useRouterPush();
    const urlCreator = useUrlCreator();
    const deviceType = useDeviceType();
    const openLink = useOpenLink();
    const productData = product?.data;
    const productCategoryLabel = productData?.category?.label;
    const productCategoryUrl = productData?.category?.url;
    const mainImageLink = productData?.cover?.sizes?.filter(
      (size) => size.size === 'L',
    )?.[0]?.link;
    const options = productData?.variants?.options || [];
    const hasThumbnails = !!options.find(
      (variant: ProductTileVariantsOptionType) => variant.thumbnail,
    );

    useEffect(
      (): void => {
        if (product?.hasFailed) {
          tagHotJarRecording(HOT_JAR_TAG.productNotLoaded);
        }
      },
      [product?.hasFailed],
    );

    const category = useMemo(
      (): ProductCategoryType => ({
        label: productCategoryLabel || '',
        url: productCategoryUrl
          ? `${urlCreator(ROUTES.category)}${getCategoryUrlParams(
            productCategoryUrl,
          )}`
          : '#',
      }),
      [productCategoryLabel, productCategoryUrl, urlCreator],
    );

    const imageUrl = useMemo(
      (): string => {
        if (mainImageLink) {
          return getCdnImageUrl(mainImageLink) || getProductFallbackImgUrl();
        }

        return getProductFallbackImgUrl();
      },
      [mainImageLink],
    );

    const getVariant = (variant: ProductTileVariantsOptionType): VariantType => {
      const fallbackUrl = hasThumbnails ? getProductFallbackImgUrl() : null;
      return {
        sku: variant.link,
        label: variant.label,
        url: urlCreator(ROUTES.product, {
          sku: variant.link,
        }),
        thumbnail: getCdnImageUrl(variant.thumbnail) || fallbackUrl,
      };
    };

    const handleProductClick = (event: MouseEvent<HTMLElement>, clickedSku?: string) => {
      const correctSku = clickedSku || productSku;
      onClick?.(correctSku);

      return openLink(event, urlCreator(ROUTES.product, {
        sku: correctSku,
      }));
    };

    const handleAddToCart = (sku: string) => {
      onAddToCart?.(sku);
    };

    const variants = options.map(getVariant);

    const injectedProps: InjectedProps = {
      deviceType,
      category,
      imageUrl,
      variants,
      openLink,
      push,
      urlCreator,
      onClick: handleProductClick,
      onAddToCart: handleAddToCart,
    };

    return <Component {...originalProps} {...injectedProps} />;
  };

  return React.memo(
    WithContainer,
    (prevProps, nextProps) => {
      switch (true) {
        case !isEqual(prevProps.styleType, nextProps.styleType):
        case !isEqual(prevProps.product, nextProps.product):
          return false;

        default:
          return true;
      }
    },
  );
};

export { withContainer };
